import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import {
  apiErrorMessage,
  getApiErrorStatus,
  isFeatureDisabledError,
} from "../../../api/apiError"
import {
  createSubstanceErrorDetailOf,
  useCreateSubstance,
} from "../../../api/substancesApi"
import { casNumberError } from "../../../utils/casValidation"

const FIELD_CLASS = "border-grey4 bg-grey2 text-white"

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <label htmlFor={htmlFor} className="text-[11.5px] font-semibold text-grey5">
      {children}
    </label>
  )
}

interface CreateSubstanceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fires on a successful create AND on "use existing substance" from the
   *  409 conflict panel — from the caller's perspective both end with a real
   *  substance id the user can act on (e.g. select it), so they share one
   *  callback rather than forcing every caller to branch on which path was
   *  taken. Just the id: the 409 body carries no more than
   *  `code`/`message`/`substance_id`, and no caller needs more than that to
   *  act on it — fetching the full row here would be a self-imposed
   *  requirement, not a real one. */
  onCreated: (substanceId: string) => void
}

export function CreateSubstanceModal({
  open,
  onOpenChange,
  onCreated,
}: CreateSubstanceModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <CreateSubstanceForm
          onCreated={onCreated}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  )
}

// Hooks live here so they mount/reset with the dialog (DialogContent unmounts
// on close — same pattern as CreateCustomerModal's CreateCustomerForm).
function CreateSubstanceForm({
  onCreated,
  onOpenChange,
}: {
  onCreated: (substanceId: string) => void
  onOpenChange: (open: boolean) => void
}) {
  const { translate } = useTranslation()
  const { mutate: createSubstance, isPending } = useCreateSubstance()

  const [name, setName] = useState("")
  const [casNumber, setCasNumber] = useState("")
  const [ecNumber, setEcNumber] = useState("")
  const [fullName, setFullName] = useState("")
  const [description, setDescription] = useState("")
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [conflict, setConflict] = useState<{
    substanceId: string
    message: string
  } | null>(null)

  // Any failure that is not a field-level or conflict answer. Shown inline:
  // the flag-off 403 is deliberately toast-less (global interceptor) and a
  // 4xx without `detail.code` is not toasted anywhere, so without this the
  // dialog would just sit there.
  const [submitError, setSubmitError] = useState<string | null>(null)

  function clearConflict() {
    if (conflict) setConflict(null)
    if (submitError) setSubmitError(null)
  }

  function validate(): boolean {
    const next: Record<string, string> = {}
    const trimmedCas = casNumber.trim()
    const trimmedEc = ecNumber.trim()

    if (!name.trim()) {
      next.name = translate("MC_MD_SUBSTANCE_CREATE_NAME_REQUIRED")
    }

    // Two distinct messages, because the two failure modes need opposite
    // advice. A shape failure earns the format hint. A check-digit failure
    // must NOT mention the format: that user got the format exactly right,
    // and being told their correctly-shaped CAS number is malformed reads as
    // the app being broken rather than as a typo in one digit.
    const casError = trimmedCas ? casNumberError(trimmedCas) : null
    if (casError) {
      next.casNumber = translate(
        casError === "check_digit"
          ? "MC_MD_SUBSTANCE_CREATE_CAS_CHECK_DIGIT"
          : "MC_MD_SUBSTANCE_CREATE_CAS_INVALID",
      )
    }

    // Mirrors the backend's IDENTIFIER_REQUIRED: at least one of CAS/EC must
    // be given, regardless of whether the CAS given (if any) is itself valid
    // — an invalid-CAS error above already covers that case separately.
    if (!trimmedCas && !trimmedEc) {
      next.identifier = translate("MC_MD_SUBSTANCE_CREATE_IDENTIFIER_REQUIRED")
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  function handleSubmit() {
    if (!validate()) return
    setConflict(null)
    setSubmitError(null)

    createSubstance(
      {
        name: name.trim(),
        casNumber: casNumber.trim() || undefined,
        ecNumber: ecNumber.trim() || undefined,
        fullName: fullName.trim() || undefined,
        description: description.trim() || undefined,
      },
      {
        onSuccess: (substance) => {
          onCreated(substance.id)
          onOpenChange(false)
        },
        onError: (err) => {
          const detail = createSubstanceErrorDetailOf(err)
          if (
            detail?.code === "SUBSTANCE_ALREADY_EXISTS" &&
            detail.substance_id
          ) {
            setConflict({
              substanceId: detail.substance_id,
              message: translate("MC_MD_SUBSTANCE_CREATE_CONFLICT_MESSAGE"),
            })
          } else if (detail?.code === "INVALID_CAS_NUMBER") {
            // Unreachable under correctly-mirrored validators, not just
            // "near": `casNumber` here is the exact string `validate()`
            // already ran through `casNumberError()` in this very closure
            // before the mutation fired, and that function is pure with no
            // external state — re-running it on the same value is
            // guaranteed to return the same null it already returned, even
            // if the user edits the field while the request is in flight
            // (React's stale-closure semantics keep this callback bound to
            // the value from submit time, not whatever is now on screen).
            // The only way this branch fires at all is a drift bug between
            // this file's `casNumberError` and the backend's `is_valid_cas`
            // (documented to mirror each other exactly) — and in that case
            // re-deriving "which check failed" from the same, already-wrong
            // frontend function would not help either. The backend's error
            // detail carries no reason to distinguish on, only the rejected
            // value, so this stays the generic fallback.
            setErrors((p) => ({
              ...p,
              casNumber: translate("MC_MD_SUBSTANCE_CREATE_CAS_INVALID"),
            }))
          } else if (detail?.code === "IDENTIFIER_REQUIRED") {
            // Should never actually round-trip since this is validated
            // client-side first — handled anyway for network races/multi-tab.
            setErrors((p) => ({
              ...p,
              identifier: translate(
                "MC_MD_SUBSTANCE_CREATE_IDENTIFIER_REQUIRED",
              ),
            }))
          } else if (isFeatureDisabledError(err)) {
            setSubmitError(translate("MC_MD_SUBSTANCE_CREATE_FEATURE_OFF"))
          } else if (getApiErrorStatus(err) === 403) {
            setSubmitError(translate("MC_MD_SUBSTANCE_CREATE_FORBIDDEN"))
          } else {
            // A >=500 is also toasted by the global interceptor; the inline
            // line keeps the reason next to the still-open dialog.
            setSubmitError(
              apiErrorMessage(err, translate, "MC_API_ERROR_GENERIC"),
            )
          }
        },
      },
    )
  }

  function handleUseExisting() {
    if (!conflict) return
    onCreated(conflict.substanceId)
    onOpenChange(false)
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translate("MC_MD_PANEL_SUBSTANCES_ADD")}</DialogTitle>
        <DialogDescription>
          {translate("MC_MD_SUBSTANCE_CREATE_DESC")}
        </DialogDescription>
      </DialogHeader>

      <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-1">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="new-substance-name">
            {translate("MC_MD_FIELD_NAME")}
          </FieldLabel>
          <Input
            id="new-substance-name"
            autoFocus
            value={name}
            placeholder={translate("MC_MD_SUBSTANCE_CREATE_NAME_PLACEHOLDER")}
            aria-invalid={!!errors.name}
            onChange={(e) => {
              setName(e.target.value)
              if (errors.name) setErrors((p) => ({ ...p, name: "" }))
              clearConflict()
            }}
            className={FIELD_CLASS}
          />
          {errors.name && (
            <span role="alert" className="text-[11px] text-red1">
              {errors.name}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="new-substance-cas">
              {translate("MC_MD_PANEL_COL_CAS")}
            </FieldLabel>
            <Input
              id="new-substance-cas"
              value={casNumber}
              placeholder={translate("MC_MD_SUBSTANCE_CREATE_CAS_PLACEHOLDER")}
              aria-invalid={!!errors.casNumber || !!errors.identifier}
              onChange={(e) => {
                setCasNumber(e.target.value)
                if (errors.casNumber || errors.identifier)
                  setErrors((p) => ({ ...p, casNumber: "", identifier: "" }))
                clearConflict()
              }}
              className={FIELD_CLASS}
            />
            {errors.casNumber && (
              <span role="alert" className="text-[11px] text-red1">
                {errors.casNumber}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="new-substance-ec">
              {translate("MC_MD_FIELD_EC_NUMBER")}
            </FieldLabel>
            <Input
              id="new-substance-ec"
              value={ecNumber}
              placeholder={translate("MC_MD_SUBSTANCE_CREATE_EC_PLACEHOLDER")}
              aria-invalid={!!errors.identifier}
              onChange={(e) => {
                setEcNumber(e.target.value)
                if (errors.identifier)
                  setErrors((p) => ({ ...p, identifier: "" }))
                clearConflict()
              }}
              className={FIELD_CLASS}
            />
          </div>
        </div>
        {errors.identifier && (
          <span role="alert" className="-mt-1.5 text-[11px] text-red1">
            {errors.identifier}
          </span>
        )}

        {/* Regulatory flags (SVHC/RoHS/etc.) are always system-derived from
            the curated DB — a user must never be able to silently mark a
            substance as unlisted if it is in fact listed. */}
        <p className="text-[11px] text-grey5">
          {translate("MC_MD_SUBSTANCE_CREATE_REGULATORY_HINT")}
        </p>

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="new-substance-full-name">
            {translate("MC_MD_SUBSTANCE_CREATE_FULL_NAME_LABEL")}
          </FieldLabel>
          <Input
            id="new-substance-full-name"
            value={fullName}
            placeholder={translate(
              "MC_MD_SUBSTANCE_CREATE_FULL_NAME_PLACEHOLDER",
            )}
            onChange={(e) => setFullName(e.target.value)}
            className={FIELD_CLASS}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="new-substance-description">
            {translate("MC_MD_SUBSTANCE_CREATE_DESCRIPTION_LABEL")}
          </FieldLabel>
          <Textarea
            id="new-substance-description"
            value={description}
            placeholder={translate(
              "MC_MD_SUBSTANCE_CREATE_DESCRIPTION_PLACEHOLDER",
            )}
            onChange={(e) => setDescription(e.target.value)}
            className={FIELD_CLASS}
            rows={3}
          />
        </div>

        {conflict && (
          <div
            role="alert"
            className="flex flex-col items-start gap-2 rounded-md border border-yellow1/30 bg-yellow1/10 p-3 text-[12px] text-white"
          >
            <p>{conflict.message}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleUseExisting}
            >
              {translate("MC_MD_SUBSTANCE_CREATE_CONFLICT_USE_EXISTING")}
            </Button>
          </div>
        )}

        {submitError && (
          <span role="alert" className="text-[12px] text-red1">
            {submitError}
          </span>
        )}
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {translate("MC_MD_EDIT_CANCEL")}
        </Button>
        <Button
          className="bg-yellow1 text-black hover:bg-yellow2"
          onClick={handleSubmit}
          disabled={isPending}
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {translate("MC_MD_PANEL_SUBSTANCES_ADD")}
        </Button>
      </DialogFooter>
    </>
  )
}
